import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { MobileNavigation } from '../components/MobileNavigation';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, Calendar, CheckCircle, QrCode, Clock, ClipboardText, GearSix, LockKey, SignOut, FirstAid, Scroll, Basketball } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../utils/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export function StaffDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [todaySessions, setTodaySessions] = useState([]);
  const [stats, setStats] = useState({
    todayCheckins: 0,
    todayRegistered: 0,
    activeSessions: 0
  });
  
  useEffect(() => {
    loadDashboardData();
  }, []);
  
  const loadDashboardData = async () => {
    try {
      // Get user info
      const userResponse = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        credentials: 'include'
      });
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
      }
      
      // Get staff sessions with counts
      const sessionsResponse = await fetch(`${getApiBaseUrl()}/api/staff/sessions`, {
        credentials: 'include'
      });
      
      if (sessionsResponse.ok) {
        const sessionsData = await sessionsResponse.json();
        
        // Filter today's sessions
        const today = new Date().toISOString().split('T')[0];
        const todaysSessions = sessionsData.filter(session => session.date === today);
        setTodaySessions(todaysSessions);
        
        // Calculate stats
        let totalCheckins = 0;
        let totalRegistered = 0;
        todaysSessions.forEach(session => {
          totalCheckins += session.checked_in_count || 0;
          totalRegistered += session.registered_count || 0;
        });
        
        setStats({
          todayCheckins: totalCheckins,
          todayRegistered: totalRegistered,
          activeSessions: todaysSessions.length
        });
      }
    } catch (error) {
      console.error('Load dashboard error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const handleLogout = async () => {
    try {
      await fetch(`${getApiBaseUrl()}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="staff-dashboard">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-heading">STAFF DASHBOARD</h1>
              <p className="text-sm text-muted-foreground">
                Welcome back{user?.name ? `, ${user.name}` : ''}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="staff-settings-menu-btn">
                  <GearSix size={24} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/change-password')} data-testid="staff-change-password-menu-item">
                  <LockKey size={18} className="mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500" data-testid="staff-logout-menu-item">
                  <SignOut size={18} className="mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Today's Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-primary">{stats.activeSessions}</div>
              <p className="text-xs text-muted-foreground mt-1">Today's Sessions</p>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-500">{stats.todayRegistered}</div>
              <p className="text-xs text-muted-foreground mt-1">Registered</p>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-500">{stats.todayCheckins}</div>
              <p className="text-xs text-muted-foreground mt-1">Checked In</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Quick Actions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading">QUICK ACTIONS</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Button 
              onClick={() => navigate('/scan')}
              className="h-24 flex flex-col items-center justify-center space-y-2 bg-green-500/10 hover:bg-green-500/20 text-green-500 border-green-500/20"
              variant="outline"
              data-testid="qr-scanner-btn"
            >
              <QrCode size={32} weight="duotone" />
              <span className="text-sm font-medium">QR Scanner</span>
            </Button>
            
            <Button 
              onClick={() => navigate('/staff/roster')}
              className="h-24 flex flex-col items-center justify-center space-y-2 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20"
              variant="outline"
              data-testid="view-roster-btn"
            >
              <ClipboardText size={32} weight="duotone" />
              <span className="text-sm font-medium">View Roster</span>
            </Button>
            
            <Button 
              onClick={() => navigate('/staff/resources')}
              className="h-24 flex flex-col items-center justify-center space-y-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 border-red-500/20"
              variant="outline"
              data-testid="eap-resources-btn"
            >
              <FirstAid size={32} weight="duotone" />
              <span className="text-sm font-medium">EAP & Staff Resources</span>
            </Button>
            
            <Button 
              onClick={() => navigate('/rules')}
              className="h-24 flex flex-col items-center justify-center space-y-2 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20"
              variant="outline"
              data-testid="program-rules-btn"
            >
              <Scroll size={32} weight="duotone" />
              <span className="text-sm font-medium">Program Rules</span>
            </Button>
            <Button 
              onClick={() => navigate('/open-run')}
              className="h-24 flex flex-col items-center justify-center space-y-2 bg-orange-500/10 hover:bg-orange-500/20 text-orange-500 border-orange-500/20"
              variant="outline"
              data-testid="game-tracker-btn"
            >
              <Basketball size={32} weight="duotone" />
              <span className="text-sm font-medium">Game Tracker</span>
            </Button>
          </CardContent>
        </Card>
        
        {/* Today's Sessions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Calendar size={20} weight="duotone" className="text-primary" />
              TODAY'S SESSIONS
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todaySessions.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <Clock size={48} weight="duotone" className="mx-auto mb-4 opacity-50" />
                <p>No sessions scheduled for today</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todaySessions.map((session) => (
                  <div 
                    key={session.session_id}
                    className="p-4 rounded-lg border border-border bg-muted/20"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium">
                          {formatTime(session.start_time)} - {formatTime(session.end_time)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {session.location?.name || 'Unknown Location'}
                        </p>
                      </div>
                      <Badge 
                        className={
                          session.checked_in_count === session.registered_count && session.registered_count > 0
                            ? 'bg-green-500'
                            : 'bg-primary'
                        }
                      >
                        {session.checked_in_count}/{session.registered_count} checked in
                      </Badge>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="w-full bg-muted rounded-full h-2 mt-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ 
                          width: `${session.registered_count > 0 
                            ? (session.checked_in_count / session.registered_count) * 100 
                            : 0}%` 
                        }}
                      />
                    </div>
                    
                    {session.waitlist_count > 0 && (
                      <p className="text-xs text-yellow-500 mt-2">
                        {session.waitlist_count} on waitlist
                      </p>
                    )}
                    
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/staff/roster?session=${session.session_id}`)}
                        className="flex-1"
                      >
                        <Users size={16} className="mr-1" />
                        View Roster
                      </Button>
                      <Button
                        size="sm"
                        className="btn-primary flex-1"
                        onClick={() => navigate('/scan')}
                      >
                        <QrCode size={16} className="mr-1" />
                        Check In
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <MobileNavigation userRole="staff" />
    </div>
  );
}
